import { FC, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { NavItem } from '@/types/common'
import { SearchInput } from '@/components/shared/SearchInput'
import logo from '@/assets/images/logo/Logo-100p.png'
import MainNavBarButton from '@/components/ui/MainNavBarButton'
import { useI18n } from '@/hooks'

interface MainNavBarProps {
  onSearch?: (query: string) => void
  className?: string
}

const MainNavBar: FC<MainNavBarProps> = ({ 
  onSearch,
  className = ''
}) => {
  const { lang, fontClass, t } = useI18n()
  const navItems: NavItem[] = [
    { id: 'home', label: t.home, href: '/' },
    { id: 'shop', label: t.shop, href: '/shop' },
    { id: 'learn', label: t.learn, href: '/learn' },
  ]
  const aboutMenuItems = [
    { id: 'story', label: t.ourStory, href: '/about?section=story' },
    { id: 'product', label: t.ourProduct, href: '/about?section=product' },
    { id: 'contact', label: t.contactUs, href: '/about?section=contact' },
    { id: 'faq', label: t.faq, href: '/about?section=faq' },
  ]
  const location = useLocation()

  const handleSearch = useCallback((query: string) => {
    if (onSearch) {
      onSearch(query)
    } else {
      console.log('Searching for:', query)
    }
  }, [onSearch])

  return (
    <nav className={`w-full bg-white shadow-sm mt-14 sticky top-14 z-40 border-b ${fontClass} ${className}`}>
      <div className="mx-auto flex h-20 max-w-5xl items-center overflow-hidden px-2 sm:h-24 sm:px-0">
        {/* Navigation Items */}
        <div className="flex flex-grow items-center justify-center gap-1 sm:gap-4 md:gap-10">
          {navItems.slice(0, 2).map((item) => (
            <Link key={item.id} to={item.href}>
              <MainNavBarButton 
                text={item.label} 
                isActive={location.pathname === item.href}
              />
            </Link>
          ))}
          
          {/* Logo Space */}
          <div className="mx-1 h-[42px] w-[52px] sm:mx-4 sm:h-[54px] sm:w-[66px]"></div>

          <div className="group relative">
            <Link to="/about?section=story">
              <MainNavBarButton 
                text={t.aboutUs}
                isActive={location.pathname === '/about'}
              />
            </Link>

            <div className="pointer-events-none absolute left-1/2 top-full z-50 w-56 -translate-x-1/2 pt-3 opacity-0 transition duration-200 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100">
              <div className="bg-white/95 py-2 backdrop-blur-sm">
                {aboutMenuItems.map(item => (
                  <Link
                    key={item.id}
                    to={item.href}
                    className="block px-5 py-3 text-center text-sm tracking-[0.18em] text-neutral-700 transition hover:bg-neutral-100 hover:text-neutral-950"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {navItems.slice(2, 3).map((item) => (
            <Link key={item.id} to="/learn">
              <MainNavBarButton 
                text={item.label} 
                isActive={location.pathname.startsWith('/learn')}
              />
            </Link>
          ))}

          {navItems.slice(3).map((item) => (
            <Link key={item.id} to={item.href}>
              <MainNavBarButton 
                text={item.label} 
                isActive={location.pathname === item.href}
              />
            </Link>
          ))}
        </div>
        
        {/* Centered Logo */}
        <Link to="/" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="flex h-[42px] w-[52px] items-center justify-center sm:h-[54px] sm:w-[66px]">
            <img
              src={logo}
              alt="Brand Logo"
              className="h-[42px] w-[52px] cursor-pointer object-contain transition hover:opacity-90 sm:h-[54px] sm:w-[66px]"
            />
          </div>
        </Link>
        
        {/* Search Input */}
        <div className="absolute right-4 top-1/2 hidden -translate-y-1/2 items-center sm:flex" style={{ height: '54px' }}>
          <SearchInput 
            lang={lang} 
            variant="expandable"
            size="sm"
            onSearch={handleSearch}
          />
        </div>
      </div>
    </nav>
  )
}

export default MainNavBar
